import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Grid from '@mui/material/Grid2';
import { Field, useFormikContext } from 'formik';
import { Input } from '@components/input';
import { CreateProjectForm } from '@pages/create-project/create-project';
import { CalculationRequestData, CalculationResponse, mapDTOToValues, } from '@api/projects/mutations';
import { UseMutateFunction } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SectionWrapper } from "@components/ui-kit/SectionWrapper";
import { MenuItem } from "@mui/material";
import { PROJECT_CONSTANTS } from "@api/projects/constants";
import { stringToNullIfEmpty } from "@components/create-project/table/helpers";

interface SecondStepProps {
  calculateBuildingPerformance: UseMutateFunction<any, Error, CalculationRequestData, unknown>;
}

export const TechStep: FC<SecondStepProps> = ({calculateBuildingPerformance}) => {
  const { t } = useTranslation();
  const {values, errors, touched, setValues} = useFormikContext<CreateProjectForm>();
  const [debouncedValues, setDebouncedValues] = useState({
    heating_performance_total: values.heating_performance_total,
    heating_consumption_total: values.heating_consumption_total,
  });
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValues({
        heating_performance_total: values.heating_performance_total,
        heating_consumption_total: values.heating_consumption_total,
      });
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [values.heating_performance_total, values.heating_consumption_total]);

  useEffect(() => {
    if (
      debouncedValues.heating_performance_total !== '' &&
      debouncedValues.heating_consumption_total !== '' &&
      !isCalculating
    ) {
      setIsCalculating(true);
      calculateBuildingPerformance({
          heated_area: values.heated_area,
          building_year: values.building_year,
          hwb_from_ea_as_planned: values.hwb_from_ea_as_planned === '' ? PROJECT_CONSTANTS.DEFAULT_HWB_FROM_EA_AS_PLANNED : values.hwb_from_ea_as_planned,
          type_of_heating_distribution_w: values.type_of_heating_distribution_w,
          type_of_warmwater_distribution_ww: values.type_of_warmwater_distribution_ww,
          type_of_warmwater_generation: stringToNullIfEmpty(values.type_of_warmwater_generation),
          heating_performance_total: debouncedValues.heating_performance_total,
          heating_consumption_total: debouncedValues.heating_consumption_total,
          "spec_heating_consumption": 48, // TODO: Replace with actual data later
          "spec_warm_water_consumption_central": 30, // TODO: Replace with actual data later
        },
        {
          onSuccess: (response: CalculationResponse) => {
            const mappedValues = mapDTOToValues(response, values);

            mappedValues.heating_performance_total = values.heating_performance_total;
            mappedValues.heating_consumption_total = values.heating_consumption_total;

            setValues(mappedValues);
            setIsCalculating(false);
          },
          onError: () => {
            toast.error('Calculation failed. Please try again.');
            setIsCalculating(false);
          },
        });
    }
  }, [debouncedValues, calculateBuildingPerformance, values.heated_area]);

  const typesOfHeatingDistribution = [
    {value: 'Central', label: 'Central'},
    {value: 'Decentral', label: 'Decentral'},
  ];

  const typesOfWarmwaterDistribution = [
    {value: 'Central', label: 'Central'},
    {value: 'Decentral', label: 'Decentral'},
  ];

  const typesOfWarmwaterGeneration = [
    {value: 'E-Speicher', label: 'E-Speicher'},
    {value: 'Gastherme für Warmwasser', label: 'Gastherme für Warmwasser'},
  ];

  return (
    <div className="flex flex-col gap-4">
      <SectionWrapper title={t('project.infrastructureBlockTitle')}>
        <Grid container spacing={3}>
          <Grid size={4}>
            <Field
              as={Input}
              select
              label={t('project.typeOfHeatingDistribution')}
              name="type_of_heating_distribution_w"
              errors={errors}
              touched={touched}
              disabled={isCalculating}
              fullWidth
              isRequired
            >
              {typesOfHeatingDistribution.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Field>
          </Grid>


          <Grid size={4}>
            <Field
              as={Input}
              select
              label={t('project.typeOfWarmwaterDistribution')}
              name="type_of_warmwater_distribution_ww"
              errors={errors}
              touched={touched}
              disabled={isCalculating}
              fullWidth
              isRequired
            >
              {typesOfWarmwaterDistribution.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Field>
          </Grid>

          <Grid size={4}>
            <Field
              as={Input}
              select
              label={t('project.typeOfWarmwaterGeneration')}
              name="type_of_warmwater_generation"
              errors={errors}
              touched={touched}
              disabled={values.type_of_warmwater_distribution_ww !== 'Decentral' || isCalculating}
              fullWidth
            >
              {typesOfWarmwaterGeneration.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Field>
          </Grid>
        </Grid>
      </SectionWrapper>

      <SectionWrapper title={t('project.totalPowerAndConsumptionBlockTitle')}>
        <Grid container spacing={3}>
          <Grid size={6}>
            <Field
              as={Input}
              label={t('project.heatingPerformanceTotal')}
              name="heating_performance_total"
              errors={errors}
              touched={touched}
              disabled={!(values.type_of_heating_distribution_w && values.type_of_warmwater_distribution_ww) || isCalculating}
              type="number"
              adornment="kW"
              fullWidth
              isRequired
            />
          </Grid>

          <Grid size={6}>
            <Field
              as={Input}
              label={t('project.heatingConsumptionTotal')}
              name="heating_consumption_total"
              errors={errors}
              touched={touched}
              disabled={!(values.type_of_heating_distribution_w && values.type_of_warmwater_distribution_ww) || isCalculating}
              type="number"
              adornment="MWh"
              fullWidth
              isRequired
            />
          </Grid>

          <Grid size={6}>
            <Field
              as={Input}
              label={t('project.heatingPower')}
              name="heating_power"
              errors={errors}
              touched={touched}
              disabled={true} // TODO: Change when BE is implemented
              type="number"
              adornment="kW"
              fullWidth
              isRequired
            />
          </Grid>

          <Grid size={6}>
            <Field
              as={Input}
              label={t('project.heatingConsumption')}
              name="heating_consumption"
              errors={errors}
              touched={touched}
              disabled={true} // TODO: Change when BE is implemented
              type="number"
              adornment="MWh"
              fullWidth
              isRequired
            />
          </Grid>

          <Grid size={6}>
            <Field
              as={Input}
              label={t('project.warmWaterPower')}
              name="warm_water_power"
              errors={errors}
              touched={touched}
              disabled={true} // TODO: Change when BE is implemented
              type="number"
              adornment="kW"
              fullWidth
              isRequired
            />
          </Grid>

          <Grid size={6}>
            <Field
              as={Input}
              label={t('project.warmWaterConsumption')}
              name="warm_water_consumption"
              errors={errors}
              touched={touched}
              disabled={true} // TODO: Change when BE is implemented
              type="number"
              adornment="MWh"
              fullWidth
              isRequired
            />
          </Grid>

          <Grid size={6}>
            <Field
              as={Input}
              label={t('project.temperatureConditioningPower')}
              name="temperature_conditioning_power"
              errors={errors}
              touched={touched}
              disabled={true} // TODO: Change when BE is implemented
              type="number"
              adornment="kW"
              fullWidth
              isRequired
            />
          </Grid>

          <Grid size={6}>
            <Field
              as={Input}
              label={t('project.temperatureConditioningConsumption')}
              name="temperature_conditioning_consumption"
              errors={errors}
              touched={touched}
              disabled={true} // TODO: Change when BE is implemented
              type="number"
              adornment="MWh"
              fullWidth
              isRequired
            />
          </Grid>
        </Grid>
      </SectionWrapper>
    </div>
  );
};
